import { ref, computed } from "vue";
import { marked } from "marked";

/**
 * Converts Airtable rich text (markdown) to sanitized HTML.
 * Airtable rich text supports: bold, italic, strikethrough, links,
 * bulleted lists, numbered lists, and line breaks.
 */
export function formatAirtableText(text) {
  if (!text) return "";
  // Convert escaped newline sequences from Airtable API into real newlines
  const processed = text.replace(/\\n/g, "\n");
  return marked.parse(processed, {
    breaks: true, // Airtable uses single newlines as line breaks
    gfm: true, // GitHub-flavored markdown (tables, strikethrough, etc.)
  });
}

export function useMakerCart() {
  const formatLearningOutcomes = (text) => {
    if (!text) return "";

    let formattedText = text.replace(/\n/g, "<br>").replace(/\\n/g, "<br>"); // Handle escaped newlines

    const hasNumberedList = /\d+\.\s/.test(formattedText);

    if (hasNumberedList) {
      const lines = formattedText.split("<br>");
      let result = "";
      let inList = false;

      lines.forEach((line) => {
        const trimmedLine = line.trim();

        if (trimmedLine.match(/^\d+\.\s/)) {
          if (!inList) {
            result += '<ol class="outcomes-list">';
            inList = true;
          }
          const content = trimmedLine.replace(/^\d+\.\s/, "");
          result += `<li>${content}</li>`;
        } else if (trimmedLine !== "") {
          if (inList) {
            result += "</ol>";
            inList = false;
          }
          result += `<p>${trimmedLine}</p>`;
        }
      });

      if (inList) {
        result += "</ol>";
      }

      return result;
    } else {
      return formattedText
        .split("<br>")
        .filter((line) => line.trim() !== "")
        .map((line) => `<p>${line.trim()}</p>`)
        .join("");
    }
  };

  return {
    formatLearningOutcomes,
  };
}

/**
 * Composable for managing tags display with expand/collapse functionality
 * @param {import('vue').Ref} dataSource - Reactive data source containing tag information
 * @param {import('vue').Ref} isMobile - Reactive boolean indicating mobile state
 * @returns {Object} Tags management utilities
 */
export function useMakercartTags(dataSource, isMobile) {
  const showAllTags = ref(false);

  const allTags = computed(() => {
    const tags = [];
    const source = dataSource.value;

    if (source.grade_level?.length) {
      tags.push(...source.grade_level);
    }

    if (source.subject) {
      tags.push(source.subject);
    }

    if (source.duration) {
      tags.push(source.duration);
    }

    if (source.hardware_associated?.length) {
      tags.push(...source.hardware_associated);
    }

    if (source.tags?.length) {
      tags.push(...source.tags);
    }

    return tags.filter(Boolean);
  });

  const visibleTags = computed(() => {
    if (showAllTags.value) return allTags.value;

    const limit = isMobile.value ? 5 : 7;
    return allTags.value.slice(0, limit);
  });

  const remainingTagsCount = computed(() => {
    if (showAllTags.value) return 0;

    const limit = isMobile.value ? 5 : 7;
    const total = allTags.value.length;

    return Math.max(total - limit, 0);
  });

  const toggleAllTags = () => {
    showAllTags.value = !showAllTags.value;
  };

  const resetTags = () => {
    showAllTags.value = false;
  };

  return {
    showAllTags,
    allTags,
    visibleTags,
    remainingTagsCount,
    toggleAllTags,
    resetTags,
  };
}
